<template>
  <div class="bg-gray-100 dark-bg-default">
    <!-- Header with ActiveTabComponent and Close Button -->
    <div class="sticky top-0 z-50 bg-white dark-bg-custom">
      <div class="flex justify-between items-center w-full min-w-0">
        <div class="flex-1 min-w-0">
          <ActiveTabComponent
            :tabs="mainTabs"
            :defaultTab="activeTab"
            @tab-changed="setActiveTab"
            :insideDialog="true"
          >
            <!-- Tab buttons slot with status chip -->
            <template #tab-buttons>
              <div class="flex items-center space-x-4">
                <h4 class="text-md font-semibold" v-if="!isLoanCase && selectedCase">
                  Status -
                  <v-chip
                    label
                    :text-color="statusColor(selectedCase?.status)?.textColor"
                    :color="statusColor(selectedCase?.status)?.bgColor"
                  >
                    {{ selectedCase?.status }}
                  </v-chip>
                </h4>
                <v-btn plain @click="closeDialog">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </template>

            <!-- Main Case Details Tab Content -->
            <template #main>
              <div class="relative h-[75vh]">
                <div class="p-2 h-full overflow-y-auto custom-scrollbar-thin pb-16">
                  <!-- Case Details Section -->
                  <div>
                    <div class="flex items-center justify-between ml-5 py-2">
                      <h4 class="text-lg font-semibold">
                        {{ caseConfig.tableTabMapping?.caseTable?.displayName || 'Case' }} Details
                      </h4>

                      <!-- Loan Status Indicators -->
                      <div v-if="isLoanCase" class="mr-5">
                        <v-chip
                          :color="
                            getStatusInfo(
                              selectedCase.loanDetails?.currentStatus || selectedCase.status
                            ).color
                          "
                          text-color="white"
                          small
                          class="mr-2 font-bold"
                        >
                          <v-icon left small>{{
                            getStatusInfo(
                              selectedCase.loanDetails?.currentStatus || selectedCase.status
                            ).icon
                          }}</v-icon>
                          {{ selectedCase.loanDetails?.currentStatus || selectedCase.status }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Loading indicator when refreshing case data -->
                    <div v-if="isRefreshingCase" class="mx-5 mb-4">
                      <v-alert type="info" dense text class="mb-0">
                        <v-progress-circular
                          indeterminate
                          size="16"
                          class="mr-2"
                        ></v-progress-circular>
                        Refreshing case data...
                      </v-alert>
                    </div>

                    <!-- Loan Completion Progress Bar -->
                    <div v-if="isLoanCase" class="mx-5 px-4 bg-white">
                      <div class="relative pt-6 pb-2">
                        <!-- Badge -->
                        <div
                          class="absolute transition-all duration-300 ease-out -top-2 transform -translate-x-1/2 bg-gray-900 text-white text-[11px] font-bold px-2 py-0.5 rounded shadow-sm flex flex-col items-center"
                          :style="{ left: `${loanCompletionPercentage}%` }"
                        >
                          <span>{{ loanCompletionPercentage }}% Completed</span>
                          <div class="w-1.5 h-1.5 bg-gray-900 rotate-45 -mb-1 mt-0.5"></div>
                        </div>

                        <!-- Progress Track -->
                        <div
                          class="w-full bg-gray-100 rounded-full h-3.5 overflow-hidden border border-gray-200/50 flex"
                        >
                          <div
                            class="h-full rounded-full transition-all duration-500 ease-out"
                            :style="{
                              width: `${loanCompletionPercentage}%`,
                              backgroundColor: '#4CAF50'
                            }"
                          ></div>
                        </div>

                        <div
                          class="flex justify-between items-center text-xs text-gray-500 font-medium mt-2"
                        >
                          <span>EMI Paid: {{ loanEmiPaidCount }}</span>
                          <span
                            >Total Tenure:
                            {{
                              selectedCase.loanDetails?.emiSchedule?.length ||
                              selectedCase.loanDetails?.tenure ||
                              0
                            }}
                            EMIs</span
                          >
                        </div>
                      </div>
                    </div>

                    <!-- Case Details - All borrowers in single view -->
                    <CaseDetailsData
                      :selectedCase="selectedCase"
                      :caseConfig="caseConfig"
                      :additionalColums="additionalColums"
                      :configLang="configLang"
                      :isLoanCase="isLoanCase"
                      :borrowerTabs="borrowerTabs"
                      :customerDetails="customerDetails"
                      :loadingCustomers="loadingCustomers"
                      :branchName="branchName"
                      @refresh-borrowers="handleRefreshBorrowers"
                      @open-customer-details="handleOpenCustomerDetails"
                      @close-dialog="closeDialog"
                    />
                  </div>
                  <!-- Loan Timeline Section (only for loan cases) -->
                  <div
                    v-if="isLoanCase && loanWorkflowSteps.length"
                    class="px-4 py-4 bg-white dark-bg-custom rounded-md"
                  >
                    <div class="flex justify-between items-center mb-2">
                      <div class="flex justify-start">
                        <h4 class="text-lg font-semibold mx-2 cursor-pointer">Timeline</h4>
                      </div>
                      <!-- <v-btn icon @click="toggleTimelineCollapse">
                          <v-icon>{{ isTimelineCollapsed ? 'mdi-arrow-down-box ' : 'mdi-arrow-up-box' }}</v-icon>
                        </v-btn> -->
                    </div>

                    <!-- Loan Timeline -->
                    <LoanTimeline :steps="loanWorkflowSteps" :isCollapsed="isTimelineCollapsed" />
                  </div>

                  <!-- Revenue Share Chart Section (only for loan cases with revenue share data) -->
                  <div v-if="isLoanCase && hasRevenueShareData" class="my-4">
                    <div class="bg-white dark-bg-custom p-6 rounded-md">
                      <div class="flex justify-between items-center mb-4">
                        <h4 class="text-lg font-semibold text-gray-800">Revenue Distribution</h4>
                        <v-chip color="blue" text-color="white" small class="ml-2">
                          <v-icon left small>mdi-chart-donut</v-icon>
                          Breakdown
                        </v-chip>
                      </div>

                      <div class="flex flex-col lg:flex-row items-center gap-6 dark-text-color">
                        <div class="w-full lg:w-1/2">
                          <apexchart
                            type="donut"
                            height="300"
                            :options="brokerPartnerChartOptions"
                            :series="brokerPartnerChartSeries"
                            id="brokerVsPartner"
                          ></apexchart>
                        </div>

                        <div class="w-full lg:w-1/2 space-y-4">
                          <div class="bg-blue-50 dark-bg-custom p-4 rounded-lg">
                            <div class="flex items-center mb-2">
                              <div class="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                              <span class="font-semibold text-blue-800">
                                {{ $storage.getUniversal('organization_name') || 'Broker' }} Share
                              </span>
                            </div>
                            <div class="flex gap-2">
                              <div class="text-lg font-bold text-blue-600">
                                {{ selectedCase?.loanDetails?.revenueShare?.brokerShare || 0 }}%
                              </div>
                              <div class="text-sm text-gray-600 mt-1">
                                - Commission earned from this loan
                              </div>
                            </div>
                          </div>

                          <div class="bg-orange-50 dark-bg-custom p-4 rounded-lg">
                            <div class="flex items-center mb-2">
                              <div class="w-4 h-4 bg-orange-400 rounded-full mr-2"></div>
                              <span class="font-semibold text-orange-800">
                                {{
                                  selectedCase?.loanDetails?.partner?.partnerName || 'NBFC Partner'
                                }}
                                Share
                              </span>
                            </div>
                            <div class="flex gap-2">
                              <div class="text-lg font-bold text-orange-600">
                                {{ selectedCase?.loanDetails?.revenueShare?.partnerShare || 0 }}%
                              </div>
                              <div class="text-sm text-gray-600 mt-1">
                                - Partner's revenue portion
                              </div>
                            </div>
                          </div>

                          <div
                            v-if="selectedCase?.loanDetails?.revenueShare?.hurdleRate"
                            class="bg-gray-50 dark-bg-custom p-4 rounded-lg"
                          >
                            <div class="flex items-center mb-2">
                              <v-icon small class="mr-2 text-gray-600">mdi-speedometer</v-icon>
                              <span class="font-semibold text-gray-800">Hurdle Rate</span>
                            </div>
                            <div class="flex gap-2">
                              <div class="text-lg font-bold text-gray-700">
                                {{ selectedCase?.loanDetails?.revenueShare?.hurdleRate }}%
                              </div>
                              <div class="text-sm text-gray-600 mt-1">
                                - Minimum return threshold
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="isLoanCase && hasRevenueShareData" class="my-4">
                    <v-card flat>
                      <v-card-title class="text-lg font-semibold"> Expected Revenue </v-card-title>
                      <v-card-text>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                          <div class="text-center p-4 bg-blue-50 dark-bg-custom rounded-lg">
                            <v-icon size="48" color="blue">mdi-account-group</v-icon>
                            <p class="text-sm font-medium text-gray-600 mt-2">
                              Total {{ selectedCase.loanDetails?.partner?.partnerName }} (Partner)
                              Profit Gain
                            </p>
                            <p class="text-lg font-bold">
                              ₹{{ formatCurrency(totalPartnerShare) }} + ₹{{
                                formatCurrency(
                                  getProcessingFee(selectedCase.loanDetails).partnerPfAmt
                                )
                              }}
                              =
                              <span class="text-lg font-bold text-blue-600"
                                >₹{{
                                  formatCurrency(
                                    totalAmount(
                                      totalPartnerShare,
                                      getProcessingFee(selectedCase.loanDetails).partnerPfAmt
                                    )
                                  )
                                }}
                              </span>
                            </p>
                            <p class="text-lg font-bold text-blue-600"></p>

                            <p class="text-xs text-gray-500">
                              ({{ selectedCase.loanDetails?.revenueShare?.hurdleRate }}% Hurdle Rate
                              + {{ selectedCase.loanDetails?.revenueShare?.partnerShare }}% Bonus
                              Profit ) + Processing Fee
                            </p>
                          </div>
                          <div class="text-center p-4 bg-green-50 dark-bg-custom rounded-lg">
                            <v-icon size="48" color="green">mdi-handshake</v-icon>
                            <p class="text-sm font-medium text-gray-600 mt-2">
                              Total {{ orgName }} (Broker) Profit Gain
                            </p>
                            <p class="text-lg font-bold">
                              ₹{{ formatCurrency(totalBrokerShare) }} + ₹{{
                                formatCurrency(
                                  getProcessingFee(selectedCase.loanDetails).brokerPfAmt
                                )
                              }}
                              =
                              <span class="text-lg font-bold text-green-600"
                                >₹{{
                                  formatCurrency(
                                    totalAmount(
                                      totalBrokerShare,
                                      getProcessingFee(selectedCase.loanDetails).brokerPfAmt
                                    )
                                  )
                                }}</span
                              >
                            </p>

                            <p class="text-xs text-gray-500">
                              ({{ selectedCase.loanDetails?.revenueShare.brokerShare }}% Bonus
                              Profit ) + Processing Fee
                            </p>
                          </div>
                        </div>
                        <div class="mt-4 text-center">
                          <p class="text-sm text-gray-600">
                            *Calculations based on hurdle rate of
                            {{ selectedCase.loanDetails?.revenueShare.hurdleRate }}% and principal
                            amount exceeding this rate
                          </p>
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div class="mx-10" v-if="!isLoanCase">
                    <!-- Visit Details Section -->
                    <h4 class="text-lg font-semibold ml-5 mt-4 py-2">
                      {{ caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit' }} Details
                    </h4>

                    <!-- If Case No is Auto-Generated -->
                    <template
                      v-if="
                        configLang?.caseNo?.isAutoGenerated === true ||
                        configLang?.caseNo?.isAutoGenerated === 'true'
                      "
                    >
                      <div v-if="emailThreadMessages && emailThreadMessages.length">
                        <!-- Ticket/Complaint Thread Timeline -->
                        <div class="space-y-6 mt-4">
                          <v-card
                            v-for="(msg, index) in emailThreadMessages"
                            :key="msg._id || index"
                            flat
                            class="border rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow duration-200"
                          >
                            <!-- Card Header -->
                            <div
                              class="px-4 py-3 flex items-center justify-between border-b"
                              :class="
                                msg.direction === 'inbound' ? 'bg-blue-50/40' : 'bg-gray-50/50'
                              "
                            >
                              <div class="flex items-center gap-3">
                                <!-- Avatar Icon -->
                                <v-avatar
                                  size="32"
                                  :color="
                                    msg.direction === 'inbound'
                                      ? 'blue lighten-4'
                                      : 'grey lighten-3'
                                  "
                                >
                                  <v-icon
                                    small
                                    :color="
                                      msg.direction === 'inbound'
                                        ? 'blue darken-2'
                                        : 'grey darken-3'
                                    "
                                  >
                                    {{
                                      msg.direction === 'inbound' ? 'mdi-account' : 'mdi-face-agent'
                                    }}
                                  </v-icon>
                                </v-avatar>
                                <!-- Sender Details -->
                                <div>
                                  <span class="font-semibold text-gray-800 text-sm">
                                    {{
                                      msg.direction === 'inbound'
                                        ? selectedCase.customer?.customerFullName ||
                                          selectedCase.customerName ||
                                          msg.from
                                        : msg.sentBy
                                        ? `${msg.sentBy.firstName} ${msg.sentBy.lastName}`.trim()
                                        : msg.from || 'System Admin'
                                    }}
                                  </span>
                                  <span class="text-xs text-gray-500 ml-1">
                                    ({{
                                      msg.direction === 'inbound' ? 'Customer' : 'Support Agent'
                                    }})
                                  </span>
                                </div>
                              </div>
                              <!-- Timestamp -->
                              <div
                                class="text-xs justify-end text-gray-800 font-bold flex items-center gap-1 ml-auto"
                              >
                                <v-icon x-small color="black">mdi-clock-outline</v-icon>
                                <span>{{
                                  $dayjs(msg.timestamp).format('DD MMM YYYY, hh:mm A')
                                }}</span>
                              </div>
                            </div>

                            <!-- Card Body -->
                            <div class="p-4 bg-white">
                              <!-- Message Recipient header -->
                              <div class="text-xs text-gray-400 mb-2">To: {{ msg.to }}</div>

                              <!-- Email Body -->
                              <div class="text-sm text-gray-700 leading-relaxed">
                                <div v-if="isHtml(msg.body)" class="w-full overflow-hidden border rounded bg-white">
                                  <iframe
                                    :srcdoc="msg.body"
                                    sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                                    frameborder="0"
                                    style="width: 100%; border: 0;"
                                    @load="resizeIframe($event)"
                                  ></iframe>
                                </div>
                                <div v-else class="whitespace-pre-wrap">{{ msg.body }}</div>
                              </div>

                              <!-- Attachments -->
                              <div
                                v-if="msg.attachments && msg.attachments.length"
                                class="mt-4 pt-3 border-t border-gray-100"
                              >
                                <div
                                  class="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"
                                >
                                  <v-icon x-small color="grey">mdi-paperclip</v-icon>
                                  <span>Attachments</span>
                                </div>
                                <div class="flex flex-wrap gap-2">
                                  <!-- <a
                                    v-for="(att, aIdx) in msg.attachments"
                                    :key="aIdx"
                                    :href="att.s3Url"
                                    :download="att.filename || 'attachment'"
                                    class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-50 border border-gray-200 text-[#21427d] hover:bg-gray-100 transition-colors"
                                  >
                                    <v-icon x-small color="primary"
                                      >mdi-file-document-outline</v-icon
                                    >
                                    <span class="max-w-[180px] truncate">{{
                                      att.filename || 'Attachment'
                                    }}</span>
                                  </a> -->
                                  <v-btn
                                    v-for="(att, aIdx) in msg.attachments"
                                    :key="aIdx"
                                    depressed
                                    outlined
                                    x-small
                                    class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-50 border border-gray-200 text-[#21427d] hover:bg-gray-100 transition-colors normal-case"
                                    @click="downloadAttachment(att, msg, aIdx)"
                                  >
                                    <v-icon x-small color="primary"
                                      >mdi-file-document-outline</v-icon
                                    >
                                    <span class="max-w-[180px] truncate">{{
                                      att.filename || 'Attachment'
                                    }}</span>
                                  </v-btn>
                                </div>
                              </div>
                            </div>
                          </v-card>
                        </div>
                      </div>

                      <div
                        v-else-if="loadingEmailThread"
                        class="flex flex-col items-center justify-center py-12"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="40"
                          class="mb-3"
                        ></v-progress-circular>
                        <span class="text-sm text-gray-500">Loading complaint thread...</span>
                      </div>

                      <div
                        v-else
                        class="flex flex-col items-center justify-center py-16 text-center bg-white border rounded-lg p-6 mt-4"
                      >
                        <v-icon size="48" color="grey lighten-1" class="mb-3"
                          >mdi-email-open-outline</v-icon
                        >
                        <h4 class="text-base font-semibold text-gray-700">
                          No Email History Found
                        </h4>
                        <p class="text-sm text-gray-500 max-w-sm mt-1 mb-4">
                          No emails have been captured for this case yet. You can start the
                          resolution process by adding a step.
                        </p>
                        <v-btn
                          color="primary"
                          small
                          @click="addVisit"
                          class="normal-case"
                          v-if="selectedCase?.customer && selectedCase.assignedTo?.length"
                        >
                          <v-icon left small>mdi-plus</v-icon>
                          Add {{ caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit' }}
                        </v-btn>
                      </div>
                    </template>

                    <!-- Original UI (Upcoming / Older Visits UI) if Case No is NOT Auto-Generated -->
                    <template v-else>
                      <!-- Pending Visits -->
                      <div v-if="pendingVisits && pendingVisits.length">
                        <div class="flex gap-5 items-center">
                          <v-divider></v-divider>
                          <h5 class="text-md font-semibold ml-2 mb-2">
                            [ Upcoming
                            {{ caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit' }} ]
                          </h5>
                          <v-divider></v-divider>
                        </div>

                        <v-expansion-panels v-model="pendingPanel" flat>
                          <v-row class="text-center px-8 dark-bg-custom">
                            <v-col class="mt-4 font-semibold px-0">{{
                              caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit/Calls'
                            }}</v-col>
                            <v-col class="mt-4 font-semibold px-0"
                              >{{
                                caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit'
                              }}
                              Details</v-col
                            >
                            <v-col
                              class="mt-4 font-semibold px-0"
                              v-if="isCallEnabled('CALL_A_CUSTOMER')"
                              >Call Frequency</v-col
                            >
                            <v-col class="mt-4 font-semibold px-0">Outcome</v-col>
                            <!-- <v-col class="mt-4 font-semibold px-0">Next Scheduled</v-col> -->
                            <v-col class="mt-4 font-semibold px-0">Status</v-col>
                          </v-row>
                          <v-expansion-panel
                            v-for="(task, index) in pendingVisits"
                            :key="'pending-' + index"
                            class="mt-3"
                          >
                            <v-expansion-panel-header class="pt-0 pb-0 penel_header px-2">
                              <div class="flex justify-between align-center w-full">
                                <v-row class="text-center">
                                  <v-col>
                                    <p class="text-base font-semibold mt-5">
                                      {{ pendingVisits.length - index }}
                                    </p>
                                  </v-col>
                                  <v-col>
                                    <p class="mt-5 font-semibold text-blue-600">
                                      {{ selectedCase.caseNo }}
                                    </p>
                                  </v-col>
                                  <v-col v-if="isCallEnabled('CALL_A_CUSTOMER')">
                                    <p class="mt-5 font-semibold">
                                      {{ Array.isArray(task.callInfo) ? task.callInfo.length : 0 }}
                                    </p>
                                  </v-col>
                                  <v-col>
                                    <p class="mt-5 font-semibold">
                                      {{ task.meetingDetails?.meetingOutCome }}
                                    </p>
                                  </v-col>
                                  <!-- <v-col>
                                  <p class="mt-5 font-semibold">
                                    {{
                                      task.meetingDetails?.nextTaskScheduledOn
                                        ? $dayjs(task.meetingDetails?.nextTaskScheduledOn).format(
                                            'DD/MM/YYYY hh:mm A'
                                          )
                                        : $dayjs(task.taskStartDateTime).format('DD/MM/YYYY hh:mm A')
                                    }}
                                  </p>
                                </v-col> -->
                                  <v-col>
                                    <v-chip
                                      small
                                      label
                                      class="mt-5 font-semibold"
                                      :text-color="statusColor(task.taskStatus).textColor"
                                      :color="statusColor(task.taskStatus).bgColor"
                                      >{{ task.taskStatus }}</v-chip
                                    >
                                  </v-col>
                                </v-row>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="text-sm">
                              <CaseTaskData
                                :task="taskData(task)"
                                :selectedCase="selectedCase"
                                :key="task._id"
                                :type="type"
                                :caseConfig="caseConfig"
                                :branchName="branchName"
                                @deleteTask="handleDeleteTask"
                                :callTab="callTab"
                                @close-dialog="closeDialog"
                              />
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </div>

                      <no-data
                        v-else
                        :title="`No Upcoming ${
                          caseConfig.tableTabMapping?.visitTable?.displayName || 'Visits'
                        }`"
                        :subTitle="`Start by adding ${
                          caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit/Calls'
                        }`"
                        :btnText="`Add ${
                          caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit'
                        }`"
                        :btnAction="addVisit"
                        :callTab="callTab"
                      />

                      <!-- Other Visits -->
                      <div v-if="otherVisits && otherVisits.length">
                        <div class="flex gap-5 items-center my-6">
                          <v-divider></v-divider>
                          <h5 class="text-md font-semibold ml-2 mb-2">
                            [ Older
                            {{ caseConfig.tableTabMapping?.visitTable?.displayName || 'Visits' }} ]
                          </h5>
                          <v-divider></v-divider>
                        </div>

                        <v-expansion-panels v-model="otherPanel" flat>
                          <v-row class="text-center px-8 dark-bg-custom">
                            <v-col class="mt-4 font-semibold px-0">{{
                              caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit/Calls'
                            }}</v-col>
                            <v-col class="mt-4 font-semibold px-0"
                              >{{
                                caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit'
                              }}
                              Details</v-col
                            >
                            <v-col
                              class="mt-4 font-semibold px-0"
                              v-if="isCallEnabled('CALL_A_CUSTOMER')"
                              >Call Frequency</v-col
                            >
                            <v-col class="mt-4 font-semibold px-0">Outcome</v-col>
                            <!-- <v-col class="mt-4 font-semibold px-0">Last Scheduled</v-col> -->
                            <v-col class="mt-4 font-semibold px-0">Status</v-col>
                          </v-row>
                          <v-expansion-panel
                            v-for="(task, index) in otherVisits"
                            :key="'other-' + index"
                            class="mt-3"
                          >
                            <v-expansion-panel-header class="pt-0 pb-0 penel_header px-2">
                              <div class="flex justify-between align-center w-full">
                                <v-row class="text-center">
                                  <v-col>
                                    <p class="text-base font-semibold mt-5">
                                      {{ otherVisits.length - index }}
                                    </p>
                                  </v-col>
                                  <v-col>
                                    <p class="mt-5 font-semibold text-blue-600">
                                      {{ selectedCase.caseNo }}
                                    </p>
                                  </v-col>
                                  <v-col v-if="isCallEnabled('CALL_A_CUSTOMER')">
                                    <p class="mt-5 font-semibold">
                                      {{ Array.isArray(task.callInfo) ? task.callInfo.length : 0 }}
                                    </p>
                                  </v-col>
                                  <v-col>
                                    <p class="mt-5 font-semibold">
                                      {{ task.meetingDetails?.meetingOutCome }}
                                    </p>
                                  </v-col>
                                  <!-- <v-col>
                                  <p class="mt-5 font-semibold">
                                    {{
                                      task.meetingDetails?.nextTaskScheduledOn
                                        ? $dayjs(task.meetingDetails?.nextTaskScheduledOn).format(
                                            'DD/MM/YYYY hh:mm A'
                                          )
                                        : ''
                                    }}
                                  </p>
                                </v-col> -->
                                  <v-col>
                                    <v-chip
                                      small
                                      label
                                      class="mt-5 font-semibold"
                                      :text-color="statusColor(task.taskStatus).textColor"
                                      :color="statusColor(task.taskStatus).bgColor"
                                      >{{ task.taskStatus }}</v-chip
                                    >
                                  </v-col>
                                </v-row>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="text-sm">
                              <CaseTaskData
                                :task="taskData(task)"
                                :selectedCase="selectedCase"
                                :key="task._id"
                                :type="type"
                                :caseConfig="caseConfig"
                                :branchName="branchName"
                                @deleteTask="handleDeleteTask"
                                :callTab="callTab"
                                @close-dialog="closeDialog"
                              />
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </div>

                      <no-data
                        v-else-if="pendingVisits && pendingVisits.length"
                        title="Nothing to Display"
                        :subTitle="`Start by adding ${
                          caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit/Calls'
                        } to your case.`"
                        :btnText="`Add ${
                          caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit/Calls'
                        }`"
                        :btnAction="addVisit"
                        :callTab="callTab"
                      />
                    </template>
                  </div>
                </div>

                <!-- Fixed/Sticky Bottom Button for Auto-Generated Cases -->
                <div
                  v-if="
                    configLang?.caseNo?.isAutoGenerated === true ||
                    configLang?.caseNo?.isAutoGenerated === 'true'
                  "
                  class="absolute bottom-6 right-6 z-10"
                >
                  <v-btn
                    color="primary"
                    small
                    @click="addVisit"
                    class="normal-case font-semibold shadow-md"
                    v-if="
                      selectedCase?.customer &&
                      selectedCase.assignedTo?.length &&
                      emailThreadMessages?.length
                    "
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Add {{ caseConfig.tableTabMapping?.visitTable?.displayName || 'Visit' }}
                  </v-btn>
                </div>
              </div>
            </template>

            <!-- Disbursement Timeline -->
            <template #disbursements>
              <div class="p-2 h-[75vh] overflow-y-auto custom-scrollbar-thin">
                <div v-if="disbursements.length > 0" class="space-y-6">
                  <!-- Loop through all disbursements -->
                  <div
                    v-for="(disbursement, index) in disbursements"
                    :key="disbursement._id || index"
                    class="bg-white dark-bg-custom p-6 border border-gray-200"
                  >
                    <!-- Disbursement Header -->
                    <div class="flex justify-between items-center mb-4">
                      <h3 class="text-lg font-semibold text-gray-800">
                        Disbursement #{{ index + 1 }}
                      </h3>
                    </div>

                    <!-- Disbursement Details Card -->
                    <div class="mb-6 p-4 bg-gray-50 dark-bg-custom">
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div class="text-sm text-gray-600 mb-1">Amount</div>
                          <div class="font-semibold text-lg text-green-600">
                            ₹{{ formatCurrency(disbursement.amount) }}
                          </div>
                        </div>
                        <div>
                          <div class="text-sm text-gray-600 mb-1">Date</div>
                          <div class="font-semibold">
                            {{ $dayjs(disbursement.date).format('DD/MM/YYYY') }}
                          </div>
                        </div>
                        <div>
                          <div class="text-sm text-gray-600 mb-1">Payment Mode</div>
                          <div class="font-semibold">
                            {{ disbursement.paymentMode || 'Not specified' }}
                          </div>
                        </div>
                        <div>
                          <div class="text-sm text-gray-600 mb-1">Reference No</div>
                          <div class="font-semibold">
                            {{ disbursement.referenceNo || '-' }}
                          </div>
                        </div>
                        <div>
                          <div class="text-sm text-gray-600 mb-1">Bank Account</div>
                          <div class="font-semibold">
                            {{ disbursement.bankAccount || '-' }}
                          </div>
                        </div>
                        <div>
                          <div class="text-sm text-gray-600 mb-1">Processed By</div>
                          <div class="font-semibold">
                            {{ disbursement.processedBy || '-' }}
                          </div>
                        </div>
                      </div>

                      <!-- Remarks Section -->
                      <div v-if="disbursement.remarks" class="mt-4 pt-4 border-t border-gray-200">
                        <div class="text-sm text-gray-600 mb-1">Remarks</div>
                        <div class="font-medium text-gray-800">
                          {{ disbursement.remarks }}
                        </div>
                      </div>
                    </div>

                    <!-- Disbursement Timeline -->
                    <div class="mt-4" v-if="getDisbursementWorkflowSteps(disbursement).length > 0">
                      <h4 class="text-md font-semibold mb-3 text-gray-700">
                        Disbursement Workflow
                      </h4>
                      <LoanTimeline
                        :steps="getDisbursementWorkflowSteps(disbursement)"
                        :isCollapsed="false"
                      />
                    </div>
                  </div>
                </div>

                <!-- No Disbursements State -->
                <div v-else class="text-center py-12">
                  <v-icon size="64" color="grey lighten-1">mdi-cash-multiple</v-icon>
                  <div class="mt-4 text-lg text-gray-600">No disbursements available</div>
                  <div class="text-sm text-gray-500 mt-2">
                    Disbursements will appear here once they are processed
                  </div>
                </div>
              </div>
            </template>

            <!-- Pre-Payment Tab Content -->
            <template #foreclosure>
              <div
                v-if="isLoanCase && isPrepaymentAllowed"
                class="px-2 pt-2 h-[75vh] overflow-y-auto custom-scrollbar-thin"
              >
                <LoanForeclosureView
                  :loanDetails="selectedCase.loanDetails"
                  :caseId="selectedCase._id"
                  :emiSchedule="selectedCase.loanDetails?.emiSchedule || []"
                  :borrowerTabs="emiTabs"
                  :selectedCase="selectedCase"
                  @foreclosure-processed="handleForeclosureProcessed"
                  @revert-loan-closure="handleRevertLoanClosure"
                />
              </div>
            </template>

            <template #schedules>
              <div v-if="isLoanCase" class="h-[75vh] overflow-y-auto custom-scrollbar-thin px-2">
                <!-- Prepayment Not Allowed Alert -->
                <v-alert v-if="!isPrepaymentAllowed" type="info" dense text class="mb-4">
                  <v-icon small class="mr-2">mdi-information</v-icon>
                  Prepayment/Foreclosure is not enabled for this loan. Contact administrator to
                  enable prepayment option.
                </v-alert>

                <!-- Tab Navigation for Loan Details -->
                <v-tabs v-model="loanTab" v-if="emiTabs.length > 1">
                  <v-tab v-for="borrower in emiTabs" :key="borrower.id">
                    <div class="text-center text-sm">
                      {{ borrower.name }}
                      <div class="text-xs text-gray-600">
                        ({{ borrower.customerData.customerFullName }})
                      </div>
                    </div>
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="loanTab">
                  <!-- EMI Schedule Tab for each borrower -->
                  <v-tab-item v-for="borrower in emiTabs" :key="borrower.id">
                    <LoanEmiScheduleView
                      :emiSchedule="getFilteredEmiSchedule(borrower.id)"
                      :revenueShare="selectedCase.loanDetails?.revenueShare"
                      :partner="selectedCase.loanDetails?.partner"
                      :borrowerId="borrower.id"
                      :borrowerName="borrower.name"
                      :selectedCase="selectedCase"
                      @update-emi="handleEmiUpdate"
                      @accept-installment="handleAcceptInstallment"
                      @revert-installment="handleRevertInstallment"
                      @show-notification="showNotification"
                    />
                  </v-tab-item>
                </v-tabs-items>
              </div>
            </template>

            <!-- Statement of Account (SOA) Tab Content -->
            <template #soa>
              <div v-if="isLoanCase" class="h-[75vh] overflow-y-auto custom-scrollbar-thin">
                <CustomerSOA
                  ref="soaRef"
                  :selectedCase="selectedCase"
                  :orgName="orgName"
                  :branchName="branchName"
                />
              </div>
            </template>

            <!-- Ledger Tab Content -->
            <template #ledger>
              <div v-if="isLoanCase" class="h-[75vh] overflow-y-auto custom-scrollbar-thin">
                <CaseLedger
                  :selectedCase="selectedCase"
                  :emiSchedule="selectedCase.loanDetails?.emiSchedule || []"
                  :branchName="branchName"
                />
              </div>
            </template>

            <!-- Additional Charges & VAS Tab -->
            <template #additionalCharges>
              <div v-if="isLoanCase" class="p-4 h-[75vh] overflow-y-auto custom-scrollbar-thin">
                <div v-if="additionalCharges.length > 0">
                  <v-card flat class="border rounded-md overflow-hidden">
                    <v-card-title class="bg-gray-50 flex justify-between items-center py-3 px-6">
                      <div class="flex items-center">
                        <v-icon color="primary" class="mr-2">mdi-cash-plus</v-icon>
                        <h4 class="text-lg font-semibold text-gray-800">
                          Additional Charges & VAS
                        </h4>
                      </div>
                      <v-chip color="primary" small label outlined>
                        {{ additionalCharges.length }} Charges
                      </v-chip>
                    </v-card-title>
                    <v-card-text class="pa-0 overflow-x-auto">
                      <v-simple-table dense class="vas-table" style="min-width: 900px">
                        <template v-slot:default>
                          <thead>
                            <tr class="bg-gray-50 border-b">
                              <th
                                class="text-left py-3 pl-6 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 20%"
                              >
                                Charge Details
                              </th>
                              <th
                                class="text-center py-3 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 10%"
                              >
                                Due Date
                              </th>
                              <th
                                class="text-left py-3 px-4 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 12%"
                              >
                                VAS Type
                              </th>
                              <th
                                class="text-left py-3 px-4 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 12%"
                              >
                                Charge Type
                              </th>
                              <th
                                class="text-right py-3 px-4 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 10%"
                              >
                                Principal
                              </th>
                              <th
                                class="text-right py-3 px-4 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 10%"
                              >
                                Tax (GST)
                              </th>
                              <th
                                class="text-right py-3 px-4 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 10%"
                              >
                                Discount
                              </th>
                              <th
                                class="text-right py-3 px-4 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 10%"
                              >
                                Waiver
                              </th>
                              <th
                                class="text-right py-3 px-4 font-bold text-xs uppercase tracking-wider text-gray-500"
                                style="width: 12%"
                              >
                                Paid Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody class="divide-y divide-gray-100">
                            <tr
                              v-for="(charge, index) in additionalCharges"
                              :key="index"
                              class="hover:bg-gray-50 transition-colors"
                              :class="{
                                'bg-red-50/50 opacity-60': charge.isDeleted,
                                'bg-red-100/60':
                                  !charge.isDeleted && Number(charge.paidAmount || 0) === 0
                              }"
                            >
                              <td class="py-3 pl-6">
                                <span
                                  class="font-semibold text-gray-900"
                                  :class="{ 'line-through text-red-600': charge.isDeleted }"
                                  >{{ charge.name || charge.feeType }}</span
                                >
                                <v-chip
                                  v-if="charge.isDeleted"
                                  x-small
                                  color="red"
                                  dark
                                  class="ml-1 px-1 font-bold"
                                  >DELETED</v-chip
                                >
                                <div
                                  class="text-xs text-gray-500 mt-1 max-w-[250px] truncate"
                                  :title="charge.remarks"
                                >
                                  {{ charge.remarks || 'No remarks' }}
                                </div>
                              </td>
                              <td class="py-3 text-center text-sm text-gray-600">
                                {{
                                  charge.dueDate ? $dayjs(charge.dueDate).format('DD/MM/YYYY') : '-'
                                }}
                              </td>
                              <td class="py-3 text-left px-4 text-sm text-gray-600 font-semibold">
                                {{ charge.vasType || '-' }}
                              </td>
                              <td class="py-3 text-left px-4 text-sm text-gray-600">
                                {{ convertToTitle(charge.chargeType) || '-' }}
                              </td>
                              <td class="py-3 text-right px-4 text-sm font-medium text-gray-700">
                                ₹{{ formatCurrency(Number(charge.amount)) }}
                              </td>
                              <td class="py-3 text-right px-4 text-sm text-gray-600">
                                ₹{{ formatCurrency(Number(charge.metaData?.gstAmount || 0)) }} (inc)
                              </td>
                              <td class="py-3 text-right px-4 text-sm text-orange-600">
                                (₹{{
                                  formatCurrency(Number(charge.metaData?.discountAmount || 0))
                                }})
                              </td>
                              <td class="py-3 text-right px-4 text-sm text-red-600">
                                (₹{{ formatCurrency(Number(charge.metaData?.waiverAmount || 0)) }})
                              </td>
                              <td class="py-3 text-right px-4 text-sm text-green-700 font-bold">
                                ₹{{ formatCurrency(Number(charge.paidAmount || 0)) }}
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                </div>
                <div v-else class="text-center py-12">
                  <v-icon size="64" color="grey lighten-1">mdi-cash-plus</v-icon>
                  <div class="mt-4 text-lg text-gray-600">No additional charges or VAS found</div>
                  <div class="text-sm text-gray-500 mt-2">
                    Charges and Value Added Services details will appear here if applicable to this
                    loan
                  </div>
                </div>
              </div>
            </template>

            <!-- Loan Documents Tab Content -->
            <template #loanDocuments>
              <div v-if="isLoanCase" class="p-6 h-[75vh] overflow-y-auto custom-scrollbar-thin">
                <div
                  v-if="
                    selectedCase.loanDetails?.documents &&
                    selectedCase.loanDetails.documents.length > 0
                  "
                >
                  <div class="overflow-x-auto border rounded-lg">
                    <v-simple-table class="overflow-hidden" style="min-width: 700px">
                      <template v-slot:default>
                        <thead>
                          <tr class="bg-gray-50">
                            <th class="text-left font-semibold text-gray-700 py-3 px-4">
                              Document Type
                            </th>
                            <th class="text-left font-semibold text-gray-700 py-3 px-4">
                              Document Name
                            </th>
                            <th class="text-left font-semibold text-gray-700 py-3 px-4">
                              Updated By
                            </th>
                            <th
                              class="text-center font-semibold text-gray-700 py-3 px-4"
                              style="width: 150px"
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(doc, idx) in selectedCase.loanDetails.documents"
                            :key="idx"
                            class="hover:bg-gray-50 transition-colors border-b last:border-0"
                          >
                            <td class="py-3 px-4 font-medium text-gray-900">
                              {{ doc.docType || 'Document' }}
                            </td>
                            <td
                              class="py-3 px-4 text-gray-700 truncate max-w-[300px]"
                              :title="doc.docName"
                            >
                              <div class="flex items-center space-x-2">
                                <v-icon
                                  size="20"
                                  :color="getFileIconInfo(getDocExt(doc.docName)).color"
                                >
                                  {{ getFileIconInfo(getDocExt(doc.docName)).icon }}
                                </v-icon>
                                <span class="truncate">{{ doc.docName }}</span>
                              </div>
                            </td>
                            <td class="py-3 px-4 text-gray-700">
                              {{ getUpdatedByName(doc) }}
                            </td>
                            <td class="py-2 px-4 text-center">
                              <v-btn
                                small
                                icon
                                color="primary"
                                class="mr-2"
                                @click="openImage(getLoanDocUrl(doc))"
                                v-tooltip="{ text: 'View/Download' }"
                              >
                                <v-icon small>mdi-eye</v-icon>
                              </v-btn>
                              <v-btn
                                small
                                icon
                                color="error"
                                @click="deleteLoanDocument(idx)"
                                v-tooltip="{ text: 'Delete' }"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </div>
                <div v-else class="text-center py-12">
                  <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
                  <div class="mt-4 text-lg text-gray-600">No documents available</div>
                  <div class="text-sm text-gray-500 mt-2">
                    Loan documents will appear here once they are uploaded
                  </div>
                </div>
              </div>
            </template>

            <!-- Loan Audit Tab Content -->
            <template #loanAudit>
              <div v-if="isLoanCase" class="h-[75vh] overflow-y-auto custom-scrollbar-thin bg-slate-50/30">
                <AuditTable
                  :loanId="selectedCase.loanDetails?._id"
                  :caseConfig="caseConfig"
                />
              </div>
            </template>
          </ActiveTabComponent>
        </div>
      </div>
    </div>

    <!-- Add Visit Dialog -->
    <v-dialog v-model="openAddVisit" max-width="600">
      <AddEditCaseTask
        v-if="openAddVisit"
        :caseConfig="caseConfig"
        :editCase="selectedCase"
        @newTaskAdded="handleAdded"
        @caseTaskAdded="cancelVisit"
      />
    </v-dialog>

    <v-dialog v-model="openCustomerDetails" max-width="1200" content-class="rounded-md">
      <CustomerDetails
        v-if="currentCustomer"
        :customer="currentCustomer"
        :closeCustomerPopUp="() => (openCustomerDetails = false)"
      />
    </v-dialog>
  </div>
</template>
<script>
import NoData from '@/components/NoData.vue'
import CaseTaskData from './CaseTaskData'
import AddEditCaseTask from './AddEditCaseTask'
import CaseDetailsData from './CaseDetailsData'
import LoanTimeline from './LoanTimeline'
import LoanEmiScheduleView from './LoanEmiScheduleView.vue'
import LoanForeclosureView from './LoanForeclosureView.vue'
import CustomerSOA from './CustomerSOA.vue'
import CaseLedger from './CaseLedger.vue'
import CustomerDetails from '../CollectionModules/CustomerDetails.vue'
import AuditTable from './CaseModules/AuditTable.vue'
import ActiveTabComponent from '@/components/ActitiveTabcomponent.vue'
import VueApexCharts from 'vue-apexcharts'
import {
  convertToTitleCase,
  getFullAddress,
  fullName,
  dayHourFormat,
  isModuleFeatureAllowed,
  calculateBrokerShareAmount
} from '@/utils/common'
import { orderStatusColors } from '@/utils/workforce/statusColors'
import { axiosUrl } from '@/utils/workforceApi'
export default {
  name: 'CaseDetails',
  data() {
    return {
      orgName: this.$storage.getUniversal('organization_code'),
      panel: 0,
      type: 'visitDetails',
      openAddVisit: false,
      pendingPanel: 0,
      otherPanel: 0,
      isTimelineCollapsed: false,
      activeTab: 'main',
      selectedDisbursementIndex: 0,
      loanTab: 0, // 0 for EMI Schedule, 1 for Foreclosure
      isRefreshingCase: false,
      customerDetails: {}, // Store fetched customer details by ID
      loadingCustomers: false,
      openCustomerDetails: false,
      currentCustomer: null,
      emailThreadMessages: [],
      loadingEmailThread: false
    }
  },
  components: {
    NoData,
    CaseTaskData,
    AddEditCaseTask,
    CaseDetailsData,
    LoanTimeline,
    LoanEmiScheduleView,
    LoanForeclosureView,
    CustomerSOA,
    CaseLedger,
    CustomerDetails,
    ActiveTabComponent,
    AuditTable,
    apexchart: VueApexCharts
  },
  props: {
    selectedCase: Object,
    caseConfig: Object,
    callTab: Boolean,
    branchName: {
      type: String,
      default: '-'
    }
  },
  watch: {
    selectedCase: {
      handler(newData, oldData) {
        if (newData) {
          this.activeTab = 'main'
          // Clear customer details when case changes and fetch new data
          if (!oldData || newData._id !== oldData._id) {
            this.customerDetails = {}
            if (this.isLoanCase) {
              this.fetchCustomerDetails()
            }
            this.fetchEmailThread()
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    mainTabs() {
      const tabs = [
        {
          key: 'main',
          label:
            `${this.caseConfig.tableTabMapping?.caseTable?.displayName} Details` || 'Case Details',
          icon: 'mdi-file-document-outline',
          component: 'CaseDetailsContent'
        }
      ]
      if (this.isLoanCase) {
        tabs.push({
          key: 'disbursements',
          label: 'Disbursements',
          icon: 'mdi-bank-transfer',
          component: 'Disbursements'
        })
      }

      if (this.isLoanCase) {
        tabs.push({
          key: 'schedules',
          label: 'EMI Schedule',
          icon: 'mdi-calendar-clock',
          component: 'EmiSchedule'
        })
        if (this.additionalCharges.length > 0) {
          tabs.push({
            key: 'additionalCharges',
            label: 'Charges(VAS)',
            icon: 'mdi-cash-plus',
            component: 'AdditionalCharges'
          })
        }
        if (this.isPrepaymentAllowed) {
          tabs.push({
            key: 'foreclosure',
            label: 'Pre-Payment',
            icon: 'mdi-cash-fast',
            component: 'LoanForeclosureView'
          })
        }
        tabs.push({
          key: 'soa',
          label: 'Statement of Account(SOA)',
          icon: 'mdi-file-pdf-box',
          component: 'SOA'
        })
        tabs.push({
          key: 'ledger',
          label: 'Customer Ledger',
          icon: 'mdi-book-open-variant',
          component: 'Ledger'
        })
        tabs.push({
          key: 'loanDocuments',
          label: 'Loan Documents',
          icon: 'mdi-file-document-multiple-outline',
          component: 'LoanDocuments'
        })
        tabs.push({
          key: 'loanAudit',
          label: 'Loan Audit',
          icon: 'mdi-history',
          component: 'AuditTable'
        })
      }

      return tabs
    },
    isLoanCase() {
      return !!this.selectedCase?.loanDetails?._id
    },
    loanEmiPaidCount() {
      if (!this.selectedCase?.loanDetails) return 0
      const emiSchedule = this.selectedCase.loanDetails.emiSchedule || []
      const paidStatuses = [
        'paid',
        'fully paid',
        'late paid',
        'late',
        'minimum amount paid',
        'late_paid'
      ]
      return emiSchedule.filter((emi) => {
        const status = (emi.emiStatus || '').toLowerCase().trim()
        return paidStatuses.includes(status)
      }).length
    },
    loanCompletionPercentage() {
      if (!this.selectedCase?.loanDetails) return 0
      const emiSchedule = this.selectedCase.loanDetails.emiSchedule || []
      const totalEmi = emiSchedule.length || this.selectedCase.loanDetails.tenure || 0
      if (totalEmi <= 0) return 0
      return Math.min(Math.round((this.loanEmiPaidCount / totalEmi) * 100), 100)
    },
    isPrepaymentAllowed() {
      return true
      return this.selectedCase?.loanDetails?.prepaymentAllowed === true
    },
    loanWorkflowSteps() {
      return this.selectedCase?.loanDetails?.loanWorkflow?.steps || []
    },
    disbursements() {
      return this.selectedCase?.loanDetails?.loanDisbursements || []
    },
    disbursementItems() {
      return this.disbursements.map((item, index) => ({
        text: `Disbursement #${index + 1} - ₹${item.amount} (${this.$dayjs(item.date).format(
          'DD/MM/YYYY'
        )})`,
        value: index
      }))
    },
    selectedDisbursement() {
      return this.disbursements[this.selectedDisbursementIndex] || null
    },
    disbursementWorkflowSteps() {
      if (this.selectedDisbursement?.workflow?.steps) {
        return this.selectedDisbursement.workflow.steps
      }
      return this.selectedDisbursement?.disbursementWorkflowSteps || []
    },
    borrowerTabs() {
      const tabs = []

      if (!this.selectedCase) return tabs

      // Add primary borrower
      if (this.selectedCase?.customer) {
        const primaryId = this.selectedCase.customer._id || this.selectedCase.customer.customerId
        tabs.push({
          id: primaryId,
          name: 'Primary Borrower',
          customerData: this.selectedCase.customer,
          share: this.getPrimaryBorrowerShare(),
          emiAmount: this.getEmiAmountForBorrower(primaryId),
          type: 'primary'
        })
      }

      // Add co-applicants/co-borrowers with fetched customer data
      if (this.selectedCase?.loanDetails?.coApplicants?.length) {
        this.selectedCase.loanDetails.coApplicants.forEach((coApplicant, index) => {
          const customerId = coApplicant.customer
          const customerData = this.customerDetails[customerId] || {
            customerFullName: 'Loading...',
            _id: customerId
          }

          tabs.push({
            id: customerId,
            name: `Co-Borrower ${index + 1}`,
            customerData: customerData,
            share: coApplicant.share || 0,
            emiAmount: this.getEmiAmountForBorrower(customerId),
            type: 'co-applicant'
          })
        })
      }

      // Add guarantors
      if (this.selectedCase?.loanDetails?.guarantors?.length) {
        this.selectedCase.loanDetails.guarantors.forEach((guarantor, index) => {
          const customerId = guarantor.customer || guarantor._id
          // Check if it's already populated in the object itself
          const customerData = guarantor.customerFullName
            ? guarantor
            : this.customerDetails[customerId] || {
                customerFullName: 'Loading...',
                _id: customerId
              }

          tabs.push({
            id: customerId,
            name: `Guarantor ${index + 1}`,
            customerData: customerData,
            share: 0,
            emiAmount: 0, // Guarantors don't usually have a direct EMI share
            type: 'guarantor'
          })
        })
      }

      return tabs
    },
    emiTabs() {
      return this.borrowerTabs.filter((tab) => tab.type !== 'guarantor')
    },
    isPreviewableDocument() {
      const previewableTypes = ['application/pdf', 'text/plain']
      return previewableTypes.includes(this.previewDocument?.type)
    },
    isImageDocument() {
      return this.previewDocument?.type?.startsWith('image/')
    },
    additionalColums() {
      if (this.selectedCase?.additionalColumns) {
        const addCol = this.$props.caseConfig?.additionalColumns
        const newColumns = Object.entries(this.selectedCase?.additionalColumns)?.map(
          ([key, obj]) => {
            const fieldType = addCol?.[key]?.fieldType
            const isAttachment =
              Array.isArray(obj) &&
              obj.length > 0 &&
              obj.some((v) => v && (v.s3_url || v.s3Url) && v.filename)
            return {
              key,
              value: obj,
              fieldType,
              isAttachment
            }
          }
        )
        console.log({ newColumns })
        return newColumns
      }
      return []
    },
    configLang() {
      return this.$props.caseConfig?.fieldMappings
    },
    pendingVisits() {
      return this.selectedCase.tasks?.filter((task) => {
        const taskDate = task?.taskStartDateTime ? this.$dayjs(task.taskStartDateTime) : null
        return (
          taskDate &&
          (taskDate.isSame(this.$dayjs(), 'day') || taskDate.isAfter(this.$dayjs())) &&
          task.taskStatus !== 'COMPLETED'
        )
      })
    },
    otherVisits() {
      return this.selectedCase.tasks?.filter((task) => {
        const taskDate = task?.taskStartDateTime ? this.$dayjs(task.taskStartDateTime) : null
        return (
          (taskDate && taskDate.isBefore(this.$dayjs(), 'day')) || task.taskStatus == 'COMPLETED'
        )
      })
    },

    // Donut Chart Configuration for Broker vs Partner Share
    brokerPartnerChartOptions() {
      const orgName = this.orgName || 'Broker'
      const partnerName = this.selectedCase?.loanDetails?.partner?.partnerName || 'NBFC Partner'

      return {
        chart: {
          type: 'donut',
          height: 300
        },
        labels: [`${orgName} Share`, `${partnerName} Share`],
        colors: ['#008FFB', '#FEB019'],
        legend: {
          position: 'bottom',
          fontSize: '14px',
          fontWeight: 500
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + '%'
          },
          style: {
            fontSize: '14px',
            fontWeight: 'bold'
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total Share',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#373d3f',
                  formatter: function () {
                    return '100%'
                  }
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val.toFixed(1) + '%'
            }
          }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                height: 250
              },
              legend: {
                position: 'bottom'
              }
            }
          }
        ]
      }
    },

    brokerPartnerChartSeries() {
      const revenueShare = this.selectedCase?.loanDetails?.revenueShare
      if (!revenueShare) {
        return [0, 0]
      }

      const brokerShare = revenueShare.brokerShare || 0
      const partnerShare = revenueShare.partnerShare || 0

      return [brokerShare, partnerShare]
    },

    hasRevenueShareData() {
      const revenueShare = this.selectedCase?.loanDetails?.revenueShare
      return revenueShare && (revenueShare.brokerShare > 0 || revenueShare.partnerShare > 0)
    },
    totalTenure() {
      return this.selectedCase?.loanDetails?.emiSchedule?.length
    },
    totalPartnerShare() {
      return this.selectedCase?.loanDetails?.emiSchedule?.reduce((total, emi) => {
        return total + this.calculatePartnerShare(emi)
      }, 0)
    },
    totalBrokerShare() {
      return this.selectedCase?.loanDetails?.emiSchedule?.reduce((total, emi) => {
        return total + this.calculateBrokerShare(emi)
      }, 0)
    },
    additionalCharges() {
      return this.selectedCase?.loanDetails?.additionalCharges || []
    }
  },
  methods: {
    isHtml(str) {
      if (!str) return false
      return /<[a-z][\s\S]*>/i.test(str)
    },
    resizeIframe(event) {
      const iframe = event.target
      if (iframe && iframe.contentWindow && iframe.contentWindow.document.body) {
        iframe.style.height = iframe.contentWindow.document.body.scrollHeight + 30 + 'px'
      }
    },
    calculatePartnerShare(emi) {
      return emi.interestAmount - this.calculateBrokerShare(emi)
    },
    calculateBrokerShare(emi) {
      const revenueShare = this.selectedCase?.loanDetails?.revenueShare
      return calculateBrokerShareAmount(
        emi,
        revenueShare,
        this.getTotalPrincipal(),
        this.totalTenure
      )
    },
    downloadAttachment(att, msg, index) {
      let url = att.s3_url || att.s3Url
      if (!url && index !== undefined) {
        const additionalAtt = this.selectedCase?.additionalColumns?.attachment?.[index]
        url = additionalAtt?.s3_url || additionalAtt?.s3Url
      }
      if (!url) {
        this.$toast.error('Attachment URL not available')
        return
      }
      const a = document.createElement('a')
      a.href = url
      a.download = att.filename || 'download'
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    getTotalPrincipal() {
      if (this.totalTenure > 0) {
        const firstEmi = this.selectedCase?.loanDetails?.emiSchedule[0]
        return parseFloat(firstEmi.outstandingAmount) || 0
      }
      return 0
    },
    async handleEmiUpdate(val) {
      try {
        const response = await this.$axios.put(
          `/collection/emi-details/${val.emiId}`,
          val.updatedEmi
        )

        // Update the EMI in the UI with the response data
        if (response.data.data && this.selectedCase?.loanDetails?.emiSchedule) {
          // Find the index of the updated EMI in the schedule
          const emiIndex = this.selectedCase.loanDetails.emiSchedule.findIndex(
            (emi) => emi._id === val.emiId
          )

          // If found, update it with the response data
          if (emiIndex !== -1) {
            this.$set(this.selectedCase.loanDetails.emiSchedule, emiIndex, response.data.data)

            this.$toast.success('EMI details updated successfully')
          }
        }

        return response.data.data
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to update EMI details'
        this.$toast.error(errorMessage)
        console.error('Error updating EMI:', error)
      }
    },
    async handleAcceptInstallment(data) {
      const { payload, print } = data
      try {
        this.isRefreshingCase = true
        const response = await this.$axios.post('/collection/accept-installment', payload)

        if (response.data && response.data.data) {
          this.$toast.success('Installment accepted successfully')
          const caseData = response.data.data
          if (caseData) {
            this.$emit('update-selected-case', caseData)
            Object.assign(this.selectedCase, caseData)
          }
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to accept installment'
        this.$toast.error(errorMessage)
        console.error('Error accepting installment:', error)
      } finally {
        this.isRefreshingCase = false
      }
    },
    async handleRevertInstallment(data) {
      const { voucherNo } = data
      try {
        this.isRefreshingCase = true
        const response = await this.$axios.post('/collection/revert-installment', {
          caseId: this.selectedCase._id,
          voucherNo
        })

        if (response.data && response.data.data) {
          this.$toast.success('Installment reverted successfully')
          const caseData = response.data.data
          if (caseData) {
            this.$emit('update-selected-case', caseData)
            Object.assign(this.selectedCase, caseData)
          }
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to revert installment'
        this.$toast.error(errorMessage)
        console.error('Error reverting installment:', error)
      } finally {
        this.isRefreshingCase = false
      }
    },
    async handleRevertLoanClosure(data) {
      const { voucherNo } = data
      try {
        this.isRefreshingCase = true
        const response = await this.$axios.post('/collection/revert-loan-closure', {
          caseId: this.selectedCase._id,
          voucherNo
        })

        if (response.data && response.data.data) {
          this.$toast.success('Closure payment reverted successfully')
          const caseData = response.data.data
          if (caseData) {
            this.$emit('update-selected-case', caseData)
            Object.assign(this.selectedCase, caseData)
          }
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to revert closure'
        this.$toast.error(errorMessage)
        console.error('Error reverting closure:', error)
      } finally {
        this.isRefreshingCase = false
      }
    },
    showNotification() {},
    formatCurrency(value) {
      const newValue = value.toFixed(2)
      return new Intl.NumberFormat('en-IN').format(newValue)
    },
    getStepStatusClass(status) {
      switch (status) {
        case 'Completed':
          return 'step-completed'
        case 'Rejected':
          return 'step-rejected'
        case 'Pending':
        default:
          return 'step-pending'
      }
    },
    getConnectorClass(currentStatus, nextStatus) {
      if (currentStatus === 'Completed' && nextStatus === 'Completed') {
        return 'connector-completed'
      } else if (currentStatus === 'Rejected' || nextStatus === 'Rejected') {
        return 'connector-rejected'
      } else {
        return ''
      }
    },
    hasDocuments(step) {
      return step.documents && step.documents.length > 0
    },
    viewDocuments(step) {
      this.selectedStep = step
      this.documentDialog = true
    },
    getDocumentUrl(doc) {
      if (!doc) return ''

      // For dummy documents, return a placeholder image based on type
      if (doc.path && doc.path.startsWith('/dummy/')) {
        if (doc.type === 'application/pdf') {
          return 'https://via.placeholder.com/150x200/f5f5f5/666666?text=PDF'
        } else if (doc.type.startsWith('image/')) {
          return 'https://via.placeholder.com/150x200/e0f7fa/0288d1?text=Image'
        } else {
          return 'https://via.placeholder.com/150x200/f5f5f5/666666?text=File'
        }
      }

      // For real documents, use the actual path
      const baseUrl = process.env.VUE_APP_S3_BASE_URL || ''
      return baseUrl + (doc.path || doc.url || '')
    },
    openDocumentPreview(doc) {
      this.previewDocument = doc
      this.previewDialog = true
    },
    getDocumentIcon(doc) {
      if (!doc || !doc.type) return 'mdi-file-outline'

      if (doc.type.startsWith('image/')) {
        return 'mdi-file-image-outline'
      } else if (doc.type === 'application/pdf') {
        return 'mdi-file-pdf-outline'
      } else if (doc.type.includes('word')) {
        return 'mdi-file-word-outline'
      } else if (doc.type.includes('excel') || doc.type.includes('spreadsheet')) {
        return 'mdi-file-excel-outline'
      } else {
        return 'mdi-file-document-outline'
      }
    },
    getDocumentIconColor(doc) {
      if (!doc || !doc.type) return 'grey'

      if (doc.type.startsWith('image/')) {
        return 'blue'
      } else if (doc.type === 'application/pdf') {
        return 'red'
      } else if (doc.type.includes('word')) {
        return 'blue darken-3'
      } else if (doc.type.includes('excel') || doc.type.includes('spreadsheet')) {
        return 'green'
      } else {
        return 'grey darken-1'
      }
    },
    downloadDocument(doc) {
      if (!doc) return

      const url = this.getDocumentUrl(doc)
      const link = document.createElement('a')
      link.href = url
      link.download = doc.name || 'document'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    isCallEnabled(feature) {
      return isModuleFeatureAllowed('ADDONS', feature)
    },
    taskData(task) {
      const newTask = {
        ...task,
        case: {
          caseNo: this.selectedCase.caseNo,
          customerName: this.selectedCase.customer?.customerFullName,
          caseStatus: this.selectedCase?.userDefinedCaseStatus
        }
      }
      return newTask
    },
    calculateTAT(item) {
      const caseStart = item.caseStartDate
      const caseEnd = item.caseEndDate
      if (caseEnd) {
        return dayHourFormat(caseStart, caseEnd)
      }
      return '-'
    },
    async handleDeleteTask(item) {
      try {
        const response = await this.$axios.delete(`/collection/case/task/${item._id}`)
        const index = this.selectedCase?.tasks.findIndex((task) => task._id === item._id)
        if (index !== -1) {
          this.selectedCase?.tasks.splice(index, 1)
        }
        const successMessage = response?.data?.message || 'Case task deleted successfully.'
        this.$toast.success(successMessage)
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || 'An error occurred while deleting the case task.'
        this.$toast.error(errorMessage)
        console.error(error)
      }
    },
    convertToTitle(str) {
      return convertToTitleCase(str)
    },
    statusColor(status) {
      const colorInfo = orderStatusColors[status]
      return colorInfo
    },
    handleOpenCustomerDetails(customer) {
      this.currentCustomer = customer
      this.openCustomerDetails = true
    },
    customerAddress(item) {
      return getFullAddress(item)
    },
    customerName(item) {
      if (item) {
        return fullName(item)
      }
    },
    addVisit() {
      this.openAddVisit = true
    },
    cancelVisit() {
      this.openAddVisit = false
    },
    handleAdded(task) {
      this.openAddVisit = false
      if (task) {
        this.selectedCase.tasks.push(task)
      }
      this.fetchEmailThread()
      setTimeout(() => {
        this.fetchEmailThread()
      }, 800)
    },
    closeDialog() {
      this.$emit('closeDialog')
    },
    setActiveTab(tab) {
      this.activeTab = tab
    },
    toggleTimelineCollapse() {
      this.isTimelineCollapsed = !this.isTimelineCollapsed
    },
    getFilteredEmiSchedule(borrowerId) {
      if (!this.selectedCase?.loanDetails?.emiSchedule) return []

      return this.selectedCase.loanDetails.emiSchedule.filter((emi) => {
        // Check if EMI belongs to this borrower
        return emi.customer === borrowerId || emi.customerId === borrowerId
      })
    },
    getSelectedCaseForBorrower(borrower) {
      // Create a modified case object with the borrower as the primary customer
      return {
        ...this.selectedCase,
        customer: borrower.customerData
      }
    },
    async handleForeclosureProcessed(data) {
      // Handle foreclosure completion
      const isFullForeclosure = data.loanClosureType === 'foreclosure'
      const successMessage = isFullForeclosure
        ? 'Loan foreclosure completed successfully'
        : 'Partial payment processed successfully'

      this.$toast.success(successMessage)

      // Update case status if needed
      if (data.caseStatus) {
        this.selectedCase.status = data.caseStatus
        this.selectedCase.userDefinedCaseStatus = data.caseStatus
      }

      // Update loan details with new information
      if (this.selectedCase.loanDetails) {
        // Update loan status based on closure type
        if (isFullForeclosure) {
          this.selectedCase.loanDetails.currentStatus = 'Foreclosed'
          this.selectedCase.loanDetails.foreclosureDate = data.foreclosureDate
          this.selectedCase.loanDetails.foreclosureAmount = data.foreclosureAmount
        } else {
          // For partial payment, update relevant fields
          if (data.tenureReduction > 0) {
            this.selectedCase.loanDetails.tenureReduced = data.tenureReduction
          }
          if (data.amountReduction > 0) {
            this.selectedCase.loanDetails.lastPrepaymentAmount = data.amountReduction
            this.selectedCase.loanDetails.lastPrepaymentDate = data.foreclosureDate
          }
        }
      }

      // Update the selected case with the fully populated case data returned from the closure API
      if (data && data.data) {
        this.$emit('update-selected-case', data.data)
        Object.assign(this.selectedCase, data.data)
      }

      // Switch to main tab (Case Details) to show updated information after a short delay
      setTimeout(() => {
        this.activeTab = 'main'
        this.loanTab = 0
        this.$toast.info('Switched to Case Details to show updated information')
      }, 1500)

      // Emit event to parent component to refresh case data
      this.$emit('case-updated', this.selectedCase)
    },
    getStatusInfo(status) {
      const statusMap = {
        Applied: { color: 'blue', icon: 'mdi-file-plus' },
        'Under Review': { color: 'amber', icon: 'mdi-magnify' },
        Sanctioned: { color: 'indigo', icon: 'mdi-check-circle' },
        'Disbursal Initiated': { color: 'cyan', icon: 'mdi-bank-transfer-out' },
        Disbursed: { color: 'teal', icon: 'mdi-bank-transfer' },
        Active: { color: 'green', icon: 'mdi-check-decagram' },
        Closed: { color: 'grey', icon: 'mdi-lock' },
        Defaulted: { color: 'red', icon: 'mdi-alert' },
        NPA: { color: 'deep-orange', icon: 'mdi-alert-octagon' },
        'Written Off': { color: 'brown', icon: 'mdi-delete-sweep' },
        Foreclosed: { color: 'red darken-4', icon: 'mdi-bank-off' },
        Deleted: { color: 'red darken-2', icon: 'mdi-delete-forever' },
        Open: { color: 'success', icon: 'mdi-folder-open' },
        Completed: { color: 'primary', icon: 'mdi-check-all' },
        PendingApprovals: { color: 'orange', icon: 'mdi-clock-alert' },
        Rejected: { color: 'error', icon: 'mdi-close-circle' },
        Settled: { color: 'deep-purple', icon: 'mdi-currency-usd' },
        Canceled: { color: 'grey darken-3', icon: 'mdi-cancel' }
      }
      // Special case for manual isDeleted flag on the case object itself
      if (this.selectedCase.isDeleted) return statusMap['Deleted']

      return statusMap[status] || { color: 'green', icon: 'mdi-bank-check' }
    },
    async fetchCustomerDetails() {
      if (!this.isLoanCase || !this.selectedCase) return

      this.loadingCustomers = true
      try {
        const customerIds = []

        // Collect all customer IDs that need to be fetched
        if (this.selectedCase?.loanDetails?.coApplicants?.length) {
          this.selectedCase.loanDetails.coApplicants.forEach((coApp) => {
            if (coApp.customer && !this.customerDetails[coApp.customer]) {
              customerIds.push(coApp.customer)
            }
          })
        }

        if (this.selectedCase?.loanDetails?.guarantors?.length) {
          this.selectedCase.loanDetails.guarantors.forEach((guarantor) => {
            const customerId =
              guarantor.customer || (!guarantor.customerFullName ? guarantor._id : null)
            if (customerId && !this.customerDetails[customerId]) {
              customerIds.push(customerId)
            }
          })
        }

        // Remove duplicates
        const uniqueCustomerIds = [...new Set(customerIds)]

        if (uniqueCustomerIds.length === 0) {
          this.loadingCustomers = false
          return
        }

        // Fetch all customers in parallel using the single API endpoint
        const customerPromises = uniqueCustomerIds.map(async (customerId) => {
          try {
            const response = await this.$axios.get(
              `${axiosUrl.customers.getCustomerByid}/${customerId}`
            )
            return { customerId, data: response.data.customer }
          } catch (error) {
            console.error(`Error fetching customer ${customerId}:`, error)
            return { customerId, data: null }
          }
        })

        const customerResults = await Promise.all(customerPromises)

        // Store the fetched customer data
        const newCustomerDetails = { ...this.customerDetails }
        customerResults.forEach(({ customerId, data }) => {
          if (data) {
            newCustomerDetails[customerId] = data
          }
        })

        this.customerDetails = newCustomerDetails
      } catch (error) {
        console.error('Error fetching customer details:', error)
      } finally {
        this.loadingCustomers = false
      }
    },
    getPrimaryBorrowerShare() {
      if (!this.selectedCase?.loanDetails?.coApplicants?.length) return 100

      const totalCoApplicantShare = this.selectedCase.loanDetails.coApplicants.reduce(
        (sum, coApp) => sum + (coApp.share || 0),
        0
      )
      return Math.max(0, 100 - totalCoApplicantShare)
    },
    getEmiAmountForBorrower(borrowerId) {
      if (!this.selectedCase?.loanDetails?.emiSchedule?.length) return 0

      const borrowerEmis = this.selectedCase.loanDetails.emiSchedule.filter(
        (emi) => emi.customer === borrowerId || emi.customerId === borrowerId
      )

      return borrowerEmis[0]?.emiAmount
    },
    async handleRefreshBorrowers(caseData) {
      // Handle the refresh borrowers event from CaseDetailsData
      if (this.isLoanCase && caseData) {
        await this.fetchCustomerDetails()
      }
    },
    getProcessingFee(loanDetails) {
      const partnerPf = (loanDetails?.processingFee || []).find((el) => !el.self)?.share || 0
      const brokerPf = (loanDetails?.processingFee || []).find((el) => el.self)?.share || 0
      const partnerPfAmt = (parseFloat(partnerPf) * this.getTotalPrincipal()) / 100
      const brokerPfAmt = (parseFloat(brokerPf) * this.getTotalPrincipal()) / 100
      return { partnerPfAmt, brokerPfAmt }
    },
    totalAmount(shareAmount, pfAmount) {
      return Number(shareAmount) + Number(pfAmount)
    },
    getDisbursementWorkflowSteps(disbursement) {
      // Return workflow steps for individual disbursement
      if (disbursement?.workflow?.steps) {
        return disbursement.workflow.steps
      }
      if (disbursement?.disbursementWorkflowSteps) {
        return disbursement.disbursementWorkflowSteps
      }

      return []
    },
    getVasStatusColor(status) {
      if (!status) return 'success'
      const s = String(status).toLowerCase()
      if (s.includes('active') || s === '1') return 'success'
      if (s.includes('pending')) return 'warning'
      if (s.includes('cancelled') || s === '0') return 'error'
      return 'grey'
    },
    async fetchEmailThread() {
      if (!this.selectedCase?._id) return
      const isAutoGenerated =
        this.configLang?.caseNo?.isAutoGenerated === true ||
        this.configLang?.caseNo?.isAutoGenerated === 'true'
      if (!isAutoGenerated) return
      this.loadingEmailThread = true
      try {
        const response = await this.$axios.get(
          `/orgSetup/inbound-email/thread/${this.selectedCase._id}`
        )
        if (response.data?.success) {
          this.emailThreadMessages = response.data?.thread?.messages || []
        }
      } catch (error) {
        console.error('Failed to fetch email thread:', error)
      } finally {
        this.loadingEmailThread = false
      }
    },
    async deleteLoanDocument(index) {
      if (!confirm('Are you sure you want to delete this document?')) {
        return
      }
      try {
        const updatedDocs = (this.selectedCase.loanDetails.documents || []).map((doc) => ({
          docType: doc.docType,
          docName: doc.docName,
          docUrl: doc.docUrl,
          updatedAt: doc.updatedAt,
          updatedBy: doc.updatedBy?._id || doc.updatedBy
        }))
        updatedDocs.splice(index, 1)

        this.isRefreshingCase = true
        const response = await this.$axios.put(`/collection/loan/${this.selectedCase._id}`, {
          documents: updatedDocs
        })

        if (response.data) {
          const freshDocs =
            response.data.data?.loanDetails?.documents ||
            response.data.loanDetails?.documents ||
            updatedDocs
          this.$set(this.selectedCase.loanDetails, 'documents', freshDocs)
          this.$toast.success('Document deleted successfully')
        }
      } catch (error) {
        console.error('Error deleting document:', error)
        this.$toast.error('Failed to delete document')
      } finally {
        this.isRefreshingCase = false
      }
    },
    getUpdatedByName(doc) {
      if (!doc || !doc.updatedBy) return '-'
      if (typeof doc.updatedBy === 'object') {
        const user = doc.updatedBy
        const firstName = user.firstName || user.systemUserId?.firstName || ''
        const lastName = user.lastName || user.systemUserId?.lastName || ''
        const name = `${firstName} ${lastName}`.trim()
        return name || 'System'
      }
      return doc.updatedBy
    },
    getLoanDocUrl(doc) {
      if (doc.docUrl) return doc.docUrl
      const crn = this.selectedCase?.customer?.crn
      if (!crn) return ''
      const orgData = JSON.parse(localStorage.getItem('orgData')) || {}
      const orgId = orgData?.id || null
      // const baseUrl = 'https://zavronfinserv.s3.ap-south-1.amazonaws.com/'
      const baseUrl = process.env.VUE_APP_S3_BASE_URL
      return `${baseUrl}${orgId}/customer/${crn}/19/${doc.docName}`
    },
    getDocExt(docName) {
      return (docName || '').split('.').pop().toLowerCase()
    },
    isImageFile(ext) {
      return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes((ext || '').toLowerCase())
    },
    getFileIconInfo(ext) {
      const extension = (ext || '').toLowerCase()
      if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extension)) {
        return { icon: 'mdi-file-image-outline', color: 'blue' }
      } else if (extension === 'pdf') {
        return { icon: 'mdi-file-pdf-box', color: 'red' }
      } else if (['doc', 'docx'].includes(extension)) {
        return { icon: 'mdi-file-word-outline', color: 'indigo' }
      } else if (['xls', 'xlsx'].includes(extension)) {
        return { icon: 'mdi-file-excel-outline', color: 'green' }
      }
      return { icon: 'mdi-file-document-outline', color: 'grey' }
    },
    openImage(url) {
      if (url) {
        window.open(url, '_blank')
      }
    }
  },
  async mounted() {
    // Fetch customer details when component mounts
    if (this.isLoanCase && this.selectedCase) {
      await this.fetchCustomerDetails()
    }
    this.fetchEmailThread()
  }
}
</script>
<style scoped>
.penel_header {
  border-bottom: 1px solid rgb(192, 189, 189);
}
</style>
